import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import type { AxiosError } from 'axios';
import AuthLayout from '@/components/Auth/AuthLayout.vue';
import AuthCardHeader from '@/components/Auth/AuthCardHeader.vue';
import CButton from '@/components/shared/CButton/CButton.vue';
import CError from '@/components/shared/CError.vue';
import { completeExternalCheckout, externalLogin } from '@/api/auth';
import { externalAuthErrorMessage } from '@/utils/authUtils';
import { notifyAngularSignIn } from '@/utils/userUtils';
import { ExternalCheckoutStatus } from '@/typings/auth/externalAuth';

// Same pace the AngularJS /verify page polls the manual sign up verification at

export default /*#__PURE__*/_defineComponent({
  __name: 'SignUpComplete',
  setup(__props) {

const POLL_INTERVAL_MS = 2000;
const MAX_WAIT_MS = 60 * 1000;
const LINK_EXPIRED_MESSAGE = 'This link expired. Please log in to continue.';

const error = ref('');
const slow = ref(false);
let pollTimeout: number | undefined;
let startedAt = Date.now();
const ticket = new URLSearchParams(window.location.search).get('ticket');

const title = computed(() => {
    if (error.value) return 'We could not open your account';
    return slow.value ? 'Almost ready' : 'Setting up your account...';
});

/**
 * Stripe sends the user here after a completed sign up checkout. Like the manual sign up, the checkout is what
 * verifies the account: we ask until the verification job is done, then the ticket opens the session.
 */
const completeSignUp = async (ticket: string) => {
    try {
        const result = await completeExternalCheckout(ticket);
        if (result.status === ExternalCheckoutStatus.Pending || !result.loginTicket) {
            if (Date.now() - startedAt >= MAX_WAIT_MS) {
                slow.value = true;
                return;
            }
            pollTimeout = window.setTimeout(() => completeSignUp(ticket), POLL_INTERVAL_MS);
            return;
        }

        await externalLogin(result.loginTicket);
        notifyAngularSignIn();
    } catch (completeError) {
        // The account exists and its checkout is completed by now: an expired ticket only means logging in the usual way
        const expired = (completeError as AxiosError<{ code?: string }>)?.response?.data?.code === 'invalid_ticket';
        error.value = expired ? LINK_EXPIRED_MESSAGE : externalAuthErrorMessage(completeError, LINK_EXPIRED_MESSAGE);
    }
};

const checkAgain = () => {
    if (!ticket) return;
    slow.value = false;
    startedAt = Date.now();
    completeSignUp(ticket);
};

onMounted(() => {
    if (!ticket) {
        error.value = 'This link is no longer valid. Please log in to continue.';
        return;
    }

    completeSignUp(ticket);
});

onBeforeUnmount(() => window.clearTimeout(pollTimeout));

return { __sfc: true,POLL_INTERVAL_MS, MAX_WAIT_MS, LINK_EXPIRED_MESSAGE, error, slow, pollTimeout, startedAt, ticket, title, completeSignUp, checkAgain, AuthLayout, AuthCardHeader, CButton, CError }
}

})